import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { brandActions } from '../../action/brandaction';
import Swal from 'sweetalert2';

const EditBrand = ({ onBrandUpdated }) => {
  const [brandData, setBrandData] = useState({
    id: '',
    brand_name: '',
    status: 'Active'
  });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  
  // Get selected brand from Redux store
  const selectedBrand = useSelector((state) => state.selectedBrand);

  // Initialize form when selectedBrand changes
  useEffect(() => {
    if (selectedBrand && selectedBrand.id) {
      setBrandData({
        id: selectedBrand.id,
        brand_name: selectedBrand.Brand || selectedBrand.brand_name || '',
        status: selectedBrand.Status || selectedBrand.status || 'Active'
      });
      setIsModalOpen(true);
    }
  }, [selectedBrand]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBrandData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleStatusChange = (e) => {
    setBrandData(prev => ({
      ...prev,
      status: e.target.checked ? 'Active' : 'InActive'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!brandData.brand_name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Brand name is required',
        confirmButtonColor: '#ffa500'
      });
      return;
    }

    setLoading(true);
    try {
      const response = await dispatch(brandActions.updateBrand(brandData.id, brandData));
      
      if (response.success) {
        // Close modal
        const closeBtn = document.getElementById('closeEditBrandModal');
        if (closeBtn) closeBtn.click();
        
        // Refresh brand list
        if (onBrandUpdated) {
          onBrandUpdated();
        }
      }
    } catch (error) {
      console.error('Error updating brand:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset when modal closes
  useEffect(() => {
    const modal = document.getElementById('edit-brand');
    
    const handleHidden = () => {
      setBrandData({
        id: '',
        brand_name: '',
        status: 'Active'
      });
      setIsModalOpen(false);
      // Clear selected brand from store
      dispatch({ type: "selectd_brand", payload: null });
    };
    
    if (modal) {
      modal.addEventListener('hidden.bs.modal', handleHidden);
    }
    
    return () => {
      if (modal) {
        modal.removeEventListener('hidden.bs.modal', handleHidden);
      }
    };
  }, [dispatch]);

  // Handle modal show event to ensure form is ready
  useEffect(() => {
    const modal = document.getElementById('edit-brand');
    
    const handleShow = () => {
      if (selectedBrand && selectedBrand.id) {
        setBrandData({
          id: selectedBrand.id,
          brand_name: selectedBrand.Brand || selectedBrand.brand_name || '',
          status: selectedBrand.Status || selectedBrand.status || 'Active'
        });
        setIsModalOpen(true);
      }
    };
    
    if (modal) {
      modal.addEventListener('show.bs.modal', handleShow);
    }
    
    return () => {
      if (modal) {
        modal.removeEventListener('show.bs.modal', handleShow);
      }
    };
  }, [selectedBrand]);

  return (
    <div className="modal fade" id="edit-brand">
      <div className="modal-dialog modal-dialog-centered custom-modal-two">
        <div className="modal-content">
          <div className="modal-header border-0 custom-modal-header">
            <div className="page-title">
              <h4>Edit Brand</h4>
            </div>
            <button
              type="button"
              className="close"
              data-bs-dismiss="modal"
              id="closeEditBrandModal"
              aria-label="Close"
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          
          {isModalOpen && (
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label>Brand Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="brand_name"
                    value={brandData.brand_name}
                    onChange={handleInputChange}
                    placeholder="Enter brand name"
                    required
                    autoFocus
                  />
                </div>
                <div className="mb-0">
                  <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                    <span className="status-label">Status</span>
                    <input
                      type="checkbox"
                      id="edit_status"
                      className="check"
                      checked={brandData.status === 'Active'}
                      onChange={handleStatusChange}
                    />
                    <label htmlFor="edit_status" className="checktoggle">
                      {brandData.status}
                    </label>
                  </div>
                </div>
                <div className="modal-footer-btn mt-3">
                  <button 
                    type="button" 
                    className="btn btn-cancel me-2" 
                    data-bs-dismiss="modal"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-submit" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Updating...
                      </>
                    ) : (
                      'Update Brand'
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
          
          {!isModalOpen && (
            <div className="modal-body text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading brand details...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

EditBrand.propTypes = {
  onBrandUpdated: PropTypes.func.isRequired,
};

export default EditBrand;
